// Prompt: Boutron-style spin classification (PRD §4a, §13).
// Looks at the abstract's conclusion vs. the results' actual certainty and grades
// whether the abstract overstates the findings.

import type { ChatRequest } from '../types';
import type { ManuscriptDocument } from '../../pdf/types';

export const PROMPT_VERSION = 'classify_spin-1.0';

const LEVELS = ['none', 'mild', 'moderate', 'severe'] as const;

const SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['level', 'rationale', 'examples'],
  properties: {
    level: { type: 'string', enum: LEVELS },
    rationale: { type: 'string', minLength: 1, maxLength: 600 },
    examples: {
      type: 'array',
      items: { type: 'string', maxLength: 300 },
      maxItems: 3,
    },
  },
} as const;

export interface SpinOutput {
  level: (typeof LEVELS)[number];
  rationale: string;
  examples: string[];
}

function abstractText(doc: ManuscriptDocument): string {
  const abs = doc.sections.find((s) => s.section === 'abstract');
  if (!abs) return '';
  return doc.rawText.slice(abs.start, abs.end).slice(0, 2500);
}

function resultsText(doc: ManuscriptDocument): string {
  const res = doc.sections.find((s) => s.section === 'results');
  if (!res) return '';
  return doc.rawText.slice(res.start, res.end).slice(0, 3500);
}

export function buildPrompt(doc: ManuscriptDocument): ChatRequest {
  const systemPrompt = `You are an editorial assistant evaluating spin in a manuscript's abstract. Spin is when the abstract's conclusion overstates what the results actually support — e.g., claiming "significantly improved" without effect sizes, or framing a null result as positive. Use the Boutron taxonomy. Grade severity: none / mild / moderate / severe. Give 1-3 short verbatim quotes from the abstract that illustrate any spin you find.`;

  const userPrompt = `Abstract:\n${abstractText(doc) || '(no abstract section detected)'}\n\nResults section (excerpt):\n${resultsText(doc) || '(no results section detected)'}\n\nReturn JSON: { "level": "none"|"mild"|"moderate"|"severe", "rationale": <≤3 sentences>, "examples": [<verbatim quotes>] }`;

  return {
    systemPrompt,
    userPrompt,
    schema: SCHEMA,
    maxTokens: 500,
    promptVersion: PROMPT_VERSION,
  };
}

export function parseResponse(json: unknown): SpinOutput | null {
  if (typeof json !== 'object' || json === null) return null;
  const o = json as Record<string, unknown>;
  if (
    typeof o.level !== 'string' ||
    !LEVELS.includes(o.level as (typeof LEVELS)[number]) ||
    typeof o.rationale !== 'string' ||
    !Array.isArray(o.examples)
  ) {
    return null;
  }
  return {
    level: o.level as SpinOutput['level'],
    rationale: o.rationale,
    examples: o.examples.filter((e): e is string => typeof e === 'string').slice(0, 3),
  };
}
